import { FrequencyBandWeights, FrequencyPreset } from '../types';

export interface FrequencyPresetConfig {
  name: string;
  description: string;
  icon: string;
  weights: FrequencyBandWeights;
}

// Default balanced weights (100% = match reference exactly)
export const DEFAULT_FREQUENCY_WEIGHTS: FrequencyBandWeights = {
  subBass: 100,
  bass: 100,
  lowMids: 100,
  mids: 100,
  highMids: 100,
  presence: 100,
  air: 100,
};

export const FREQUENCY_PRESETS: Record<FrequencyPreset, FrequencyPresetConfig> = {
  balanced: {
    name: 'Balanced',
    description: 'Equal matching across all frequencies',
    icon: 'BAL',
    weights: {
      subBass: 100,
      bass: 100,
      lowMids: 100,
      mids: 100,
      highMids: 100,
      presence: 100,
      air: 100,
    },
  },

  warm: {
    name: 'Warm',
    description: 'Reduced high-mids, smooth and pleasant',
    icon: 'WARM',
    weights: {
      subBass: 110,    // Slightly boost low end
      bass: 105,       // More bass presence
      lowMids: 100,    // Keep natural
      mids: 95,        // Slightly reduce
      highMids: 60,    // REDUCE HARSHNESS ⭐
      presence: 80,    // Reduce brightness
      air: 90,         // Keep some sparkle
    },
  },

  bright: {
    name: 'Bright',
    description: 'Enhanced highs and presence',
    icon: 'BRIGHT',
    weights: {
      subBass: 90,     // Reduce rumble
      bass: 95,        // Less bass emphasis
      lowMids: 100,    // Keep natural
      mids: 105,       // Slight boost for clarity
      highMids: 120,   // Boost presence
      presence: 130,   // More sparkle
      air: 110,        // Enhance air
    },
  },

  'vocal-focus': {
    name: 'Vocal Focus',
    description: 'Clear vocals, controlled harshness',
    icon: 'VOCAL',
    weights: {
      subBass: 80,     // Reduce low rumble
      bass: 90,        // Less bass competition
      lowMids: 95,     // Slight reduction
      mids: 110,       // Boost vocal range
      highMids: 70,    // Control sibilance ⭐
      presence: 100,   // Keep clarity
      air: 95,         // Slight reduction
    },
  },

  'bass-heavy': {
    name: 'Bass Heavy',
    description: 'Enhanced low-end, controlled highs',
    icon: 'BASS',
    weights: {
      subBass: 130,    // Strong sub-bass
      bass: 120,       // Powerful bass
      lowMids: 105,    // More body
      mids: 95,        // Slight reduction
      highMids: 75,    // Reduce harshness ⭐
      presence: 85,    // Control brightness
      air: 90,         // Moderate air
    },
  },

  custom: {
    name: 'Custom',
    description: 'Manual control over all bands',
    icon: 'CUSTOM',
    weights: DEFAULT_FREQUENCY_WEIGHTS,
  },
};

export const getFrequencyPreset = (preset: FrequencyPreset): FrequencyBandWeights => {
  return FREQUENCY_PRESETS[preset].weights;
};

export const getAvailableFrequencyPresets = () => {
  return Object.entries(FREQUENCY_PRESETS)
    .filter(([key]) => key !== 'custom')
    .map(([key, config]) => ({
      value: key as FrequencyPreset,
      label: config.name,
      description: config.description,
      icon: config.icon,
    }));
};

// Helper to apply frequency preset to config
export const applyFrequencyPreset = (
  preset: FrequencyPreset,
  currentWeights?: FrequencyBandWeights
): FrequencyBandWeights => {
  if (preset === 'custom' && currentWeights) {
    return currentWeights;
  }
  return getFrequencyPreset(preset);
};